import React, { useEffect, useState } from 'react';
import {
  Badge, Box, Button, Checkbox, Collapse, FormControl, FormLabel, HStack, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Spinner, Table, Tbody, Td, Text, Textarea, Th, Thead, Tr, useBreakpointValue, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import {
  getContractBody, saveContractBody, getContractNumbers, ContractNumbers, useRegenerateContract,
} from '../hooks/useContracts';
import { useConfirm } from './ConfirmProvider';
import { SqlDateTimeToDateField } from '../utils/SqlTools';

// item 1428 — the merge prints the Effective Date as "September 10, 2026" (server: "MMMM d, yyyy")
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const longDate = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};
const countOf = (hay: string, needle: string): number => (needle ? hay.split(needle).length - 1 : 0);

// item 910 (Chad) — the REVIEW editor: adjust a DRAFT contract's merged body before
// finalizing. Saving re-renders the stored PDF server-side so Preview/Word/Send match.
// Bold = **text**, headings = # / ## at line start, bullets = "- " (same dialect the
// templates use).
const ContractBodyModal: React.FC<{
  contractId: string | null;
  onClose: () => void;
}> = ({ contractId, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  // item 1508 (Chad, iPhone screenshots) — the review editor is full-screen below md (Chakra
  // 1.6.7's Modal takes no responsive size object, hence the hook); 6xl at md+ as before
  const modalSize = useBreakpointValue({ base: 'full', md: '6xl' }) ?? '6xl';
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // item 1428 (Chad: "I have no way of changing the effective date in the header") — the header
  // date, editable here; the body mentions the same date wherever the merge printed it, so offer
  // to swap those too (count shown; default on).
  // item 1102 (Chad's idea 2026-08-12) — the NUMBERS panel: every merged number, its PROVENANCE,
  // and an orange flag when the stored text no longer carries what the deal says today. Re-pull =
  // the same rebuild as Regenerate (draft only), then the body + numbers reload.
  const [numbers, setNumbers] = useState<ContractNumbers | null>(null);
  const [numbersOpen, setNumbersOpen] = useState(true);
  const regenerator = useRegenerateContract();
  const confirm = useConfirm();
  const loadNumbers = (id: string) => getContractNumbers(id).then(setNumbers).catch(() => setNumbers(null));
  const panelBorder = useColorModeValue('gray.200', 'whiteAlpha.300'); // item 958 — light-mode contrast
  const [originalDate, setOriginalDate] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [replaceInBody, setReplaceInBody] = useState(true);
  const dateChanged = !!effectiveDate && effectiveDate !== originalDate;
  const mentions = dateChanged ? countOf(body, longDate(originalDate)) : 0;

  useEffect(() => {
    if (!contractId) return;
    setLoading(true);
    setNumbers(null);
    loadNumbers(contractId);
    getContractBody(contractId)
      .then((r) => {
        setBody(r.body);
        const d = SqlDateTimeToDateField(r.effectiveDate) || '';
        setOriginalDate(d);
        setEffectiveDate(d);
        setReplaceInBody(true);
      })
      .catch(() => toast({ status: 'error', title: 'Could not load the contract body.' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const save = async () => {
    if (!contractId) return;
    setSaving(true);
    try {
      await saveContractBody(contractId, body, dateChanged ? effectiveDate : undefined, dateChanged && replaceInBody);
      queryClient.invalidateQueries('contracts');
      toast({ status: 'success', title: 'Adjustments saved', description: 'The PDF and Word versions were re-rendered.' });
      onClose();
    } catch (e: any) {
      toast({ status: 'error', title: 'Could not save', description: String(e?.response?.data ?? 'Try again.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!contractId} onClose={onClose} size={modalSize} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Adjust contract (review)</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="xs" color="gray.500" mb={2}>
            Bold = **text** · headings = # or ## at line start · bullets = &quot;- &quot;. Saving
            re-renders the PDF and Word versions. Finalize when the review is done — only a
            finalized contract can be sent for signature.
          </Text>
          {/* item 1102 — NUMBERS: provenance + disagreement, before anyone opens the PDF */}
          {!loading && numbers && (numbers.rows.length > 0 || numbers.note) && (
            <Box mb={3} borderWidth="1px" borderColor={panelBorder} borderRadius="md" p={2}>
              <HStack justify="space-between" mb={numbersOpen ? 2 : 0}>
                <HStack spacing={2}>
                  <Text fontSize="xs" fontWeight="bold" letterSpacing="0.08em">NUMBERS</Text>
                  {(() => {
                    const off = numbers.rows.filter((r) => r.inDocument === false).length;
                    if (off > 0) return <Badge colorScheme="orange" variant="solid" fontSize="0.6rem">{`${off} DISAGREE${off === 1 ? 'S' : ''} WITH THE DEAL`}</Badge>;
                    if (numbers.rows.length > 0) return <Badge colorScheme="green" variant="subtle" fontSize="0.6rem">MATCHES THE DEAL</Badge>;
                    return null;
                  })()}
                </HStack>
                <HStack spacing={1}>
                  {numbers.isDraft && numbers.rows.length > 0 && (
                    <Button
                      size="xs"
                      variant="outline"
                      isLoading={regenerator.isLoading}
                      onClick={async () => {
                        if (!contractId) return;
                        if (!(await confirm('Re-pull every number from the deal? This rebuilds the draft from the template and today\'s pricing — hand edits to the text are replaced.', { title: 'Re-pull all numbers', confirmLabel: 'Re-pull' }))) return;
                        regenerator.mutate({ id: contractId }, {
                          onSuccess: (res: any) => {
                            const fills = res?.data?.fills ?? [];
                            getContractBody(contractId).then((r) => setBody(r.body)).catch(() => undefined);
                            loadNumbers(contractId);
                            toast({
                              status: fills.length ? 'warning' : 'success',
                              title: fills.length ? 'Re-pulled — some blanks still need answers' : 'Numbers re-pulled from the deal',
                              description: fills.length ? 'Use Regenerate on the contract row to answer the remaining blanks.' : 'The text below was rebuilt; save to re-render the PDF.',
                            });
                          },
                          onError: (e: any) => { toast({ status: 'error', title: 'Could not re-pull', description: String(e?.response?.data ?? 'Try again.') }); },
                        });
                      }}
                    >
                      Re-pull all
                    </Button>
                  )}
                  <Button size="xs" variant="ghost" onClick={() => setNumbersOpen((v) => !v)}>{numbersOpen ? 'Hide' : 'Show'}</Button>
                </HStack>
              </HStack>
              <Collapse in={numbersOpen} animateOpacity>
                {numbers.note && <Text fontSize="xs" color="gray.500">{numbers.note}</Text>}
                {numbers.rows.length > 0 && (
                  // item 1508 (Chad, iPhone screenshots) — the four-column NUMBERS table scrolls inside
                  // its own box on a phone instead of widening the modal body. A plain Box, not
                  // TableContainer: that one forces white-space nowrap on every cell and would reflow
                  // the desktop columns.
                  <Box overflowX="auto">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Number</Th>
                          <Th>Deal says today</Th>
                          <Th>Source</Th>
                          <Th>In document</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {numbers.rows.map((r) => (
                          <Tr key={r.label}>
                            <Td fontSize="xs" maxW="260px" whiteSpace="normal">{r.label}</Td>
                            <Td fontSize="xs" fontWeight="semibold">{r.suggestion ?? '—'}</Td>
                            <Td fontSize="xs" color="gray.500" whiteSpace="normal">{r.source}</Td>
                            <Td fontSize="xs">
                              {r.inDocument === null && <Badge variant="outline" colorScheme="gray">typed</Badge>}
                              {r.inDocument === true && <Badge colorScheme="green" variant="subtle">matches</Badge>}
                              {r.inDocument === false && <Badge colorScheme="orange" variant="solid">not found</Badge>}
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
                <Text fontSize="xs" color="gray.500" mt={1}>
                  &quot;Deal says today&quot; is what a fresh merge would print. Orange = the stored text does not carry that value (a source moved, or someone typed a different number). Re-pull rebuilds the draft; Regenerate on the row does the same with a fill-in dialog.
                </Text>
              </Collapse>
            </Box>
          )}
          {!loading && (
            <HStack spacing={4} mb={3} align="flex-end" flexWrap="wrap">
              <FormControl w="auto">
                <FormLabel fontSize="xs" mb={1}>Effective Date (header)</FormLabel>
                <Input type="date" size="sm" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} w="170px" />
              </FormControl>
              {dateChanged && (
                <Checkbox size="sm" isChecked={replaceInBody} onChange={(e) => setReplaceInBody(e.target.checked)} pb={1}>
                  {`Also change "${longDate(originalDate)}" in the text (${mentions} ${mentions === 1 ? 'mention' : 'mentions'}) to "${longDate(effectiveDate)}"`}
                </Checkbox>
              )}
            </HStack>
          )}
          {loading ? <Spinner /> : (
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              fontFamily="mono"
              fontSize="xs"
              minH="60vh"
              spellCheck={false}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose}>Cancel</Button>
          <Button colorScheme="brandGreen" onClick={save} isLoading={saving} isDisabled={loading}>
            Save adjustments
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ContractBodyModal;
